import styled from "@emotion/styled";
import React from "react";
import { default as RoundedPlusIcon } from "../../../public/assets/rounded-plus-icon.svg?react";
import { certificationFull } from "../../certification";
import { AppStateContext } from "../../context/AppStateContext";
import useCaregiver from "../../hooks/useCaregiver";
import { isLoading } from "../../Loading";
import { AgencyId, CaregiverCertification } from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { formatDate } from "../../utils/dateUtils";
import { AppIcon } from "../Generic/AppIcon";
import Flex from "../Generic/Flex";
import Scaffold from "../Scaffold/Scaffold";
import { withMedflytAgencyId } from "../withMedflytAgencyId";

interface Props {
  medflytAgencyId: AgencyId;
  onClickGoBack: () => void;
}

const roundedPlusIcon = <RoundedPlusIcon />;

const VirtualIdCardActivity = (props: Props) => {
  const appState = React.useContext(AppStateContext);
  const caregiver = useCaregiver(appState);

  if (isLoading(caregiver)) {
    return null;
  }

  const appAgenciesState = appState.appAgenciesState;

  if (isLoading(appAgenciesState)) {
    return null;
  }

  const medflytAgencyHireDate = caregiver.agenciesCaregiverHireDates.find(
    (agency) => agency.agencyId === props.medflytAgencyId
  );

  const medflytAgencyCertifications = caregiver.agenciesCaregiverCertifications.find(
    (agency) => agency.agencyId === props.medflytAgencyId
  );

  if (medflytAgencyHireDate === undefined || medflytAgencyCertifications === undefined) {
    return null;
  }

  const caregiverFullName = [caregiver.firstName, caregiver.lastName].join(" ");

  return (
    <Scaffold title={translate(STRINGS.VIRTUAL_ID_CARD)} onClickGoBack={props.onClickGoBack}>
      <VirtualIdCardActivity.Root>
        <VirtualIdCardActivity.Container>
          <VirtualIdCardActivity.HeadingBackground>
            <AppIcon
              icon={roundedPlusIcon}
              size={61}
              style={{ width: "61px", marginInlineStart: "8px", marginTop: "27px" }}
            />
            <AppIcon
              icon={roundedPlusIcon}
              size={30}
              style={{ width: "30px", marginTop: "10px" }}
            />
            <AppIcon
              icon={roundedPlusIcon}
              size={60}
              style={{ width: "60px", marginInlineStart: "10px", marginTop: "-30px" }}
            />
            <AppIcon
              icon={roundedPlusIcon}
              size={92}
              style={{ width: "92px", marginInline: "-30px", marginTop: "20px" }}
            />
          </VirtualIdCardActivity.HeadingBackground>
          <VirtualIdCardActivity.CaregiverPhoto>
            <img height={80} width={80} src={caregiver.photoUrl ?? "assets/default_profile.png"} />
          </VirtualIdCardActivity.CaregiverPhoto>
          <VirtualIdCardActivity.CaregiverName>
            {caregiverFullName}
          </VirtualIdCardActivity.CaregiverName>
          <VirtualIdCardActivity.CaregiverCertification>
            <span>
              {medflytAgencyCertifications.certifications.includes(
                CaregiverCertification.wrap("PCA")
              )
                ? certificationFull("PCA")
                : null}
            </span>
            <span>
              {medflytAgencyCertifications.certifications.includes(
                CaregiverCertification.wrap("HHA")
              )
                ? certificationFull("HHA")
                : null}
            </span>
          </VirtualIdCardActivity.CaregiverCertification>
          <VirtualIdCardActivity.CaregiverDetails>
            <Flex direction="row" gap={16}>
              <VirtualIdCardActivity.CaregiverDetailCaption>
                {translate(STRINGS.ID)}:
              </VirtualIdCardActivity.CaregiverDetailCaption>
              <VirtualIdCardActivity.CaregiverDetail>
                {caregiver.id}
              </VirtualIdCardActivity.CaregiverDetail>
            </Flex>
            <Flex direction="row" gap={16}>
              <VirtualIdCardActivity.CaregiverDetailCaption>
                {translate(STRINGS.DOB)}:
              </VirtualIdCardActivity.CaregiverDetailCaption>
              <VirtualIdCardActivity.CaregiverDetail>
                {caregiver.birthDateDate !== null ? formatDate(caregiver.birthDateDate) : null}
              </VirtualIdCardActivity.CaregiverDetail>
            </Flex>
            <Flex direction="row" gap={16}>
              <VirtualIdCardActivity.CaregiverDetailCaption>
                {translate(STRINGS.HIRE_DATE)}:
              </VirtualIdCardActivity.CaregiverDetailCaption>
              <VirtualIdCardActivity.CaregiverDetail>
                {medflytAgencyHireDate.hireDate !== null
                  ? formatDate(medflytAgencyHireDate.hireDate)
                  : null}
              </VirtualIdCardActivity.CaregiverDetail>
            </Flex>
          </VirtualIdCardActivity.CaregiverDetails>
          <VirtualIdCardActivity.AgencyLogo>
            <VirtualIdCardActivity.Image src="assets/medflyt-agency-logo.svg" />
          </VirtualIdCardActivity.AgencyLogo>
        </VirtualIdCardActivity.Container>
      </VirtualIdCardActivity.Root>
    </Scaffold>
  );
};

VirtualIdCardActivity.Image = styled.img`
  width: 128px;
`;

VirtualIdCardActivity.Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

VirtualIdCardActivity.HeadingBackground = styled.div`
  background: #3077eb;
  display: flex;
  flex-direction: row;
  gap: 24.5px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  width: 100%;
  height: 94px;
`;

VirtualIdCardActivity.CaregiverPhoto = styled.div`
  width: 80px;
  height: 80px;
  box-shadow: 0px 4px 16px rgba(123, 130, 141, 0.1);
  border-radius: 80px;
  border: 4px solid #ffffff;
  position: relative;
  top: -40px;
  background-color: var(--gray-500);
  object-fit: cover;

  img {
    border-radius: 80px;
  }
`;

VirtualIdCardActivity.CaregiverName = styled.div`
  font-weight: 700;
  font-size: 20px;
  position: relative;
  margin-top: -16px;
`;

VirtualIdCardActivity.CaregiverCertification = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: var(--gray-500);
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

VirtualIdCardActivity.CaregiverDetails = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

VirtualIdCardActivity.CaregiverDetail = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

VirtualIdCardActivity.Container = styled.div`
  background: #ffffff;
  box-shadow: 0px 4px 16px rgba(123, 130, 141, 0.1);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
`;

VirtualIdCardActivity.CaregiverDetailCaption = styled.div`
  font-weight: 700;
  font-size: 14px;
  width: 68px;
`;

VirtualIdCardActivity.AgencyLogo = styled.div`
  border-top: 1px solid var(--gray-100);
  padding: 24px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
`;

export default withMedflytAgencyId(VirtualIdCardActivity);
